import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Container,
  TextField,
  Grid2,
  Grid,
  Tooltip,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import { ReactComponent as AutomateIcon } from "../../../images/automateIcon.svg";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import CommonDownload from "../../custom-components/CommonDownload";
import FullscreenToggle from "../../custom-components/FullScreenToggle";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { ReactComponent as ExportButton } from "../../../images/exportButton.svg";
import { ReactComponent as ExportButton1 } from "../../../images/exportIcon.svg";
import { exportBugReportToJira } from "../../../services/webServices";
import SelectionErrorPopUp from "../../popup-components/SelectionerrorPop";
import ConfirmationDialog from "../../popup-components/ExportConfirmationPopup";
import CommonAccordion from "../../custom-components/CommonAccordion";
import createWordData from "../../../utils/WordListRenderer";
import formatDataForPDF from "../../../utils/FormatDataForPDF";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ALMConnectionPage from "../../main-pages/input-pages/ALMConnection/ALMConnectionPage";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CommonCopy from "../../custom-components/CommonCopy";

const itemsPerPage = 10;
const RenderMultipleInputBugDetils = ({
  items,
  page,
  expandedStates,
  handlers,
  setPage,
}) => {
  const [titles, setTitles] = useState(items.map((item) => item.title || ""));
  const currentItems = items.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  // const isAnyExpanded = expandedStates.some((expanded) => expanded);
  const allExpanded =
    expandedStates.length > 0 &&
    expandedStates.slice(0, items.length).every((expanded) => expanded);
  const [activeThumb, setActiveThumb] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleCopyClickData = () => {
    const orderedKeys = [
      "title",
      "description",
      "environment",
      "priority",
      "stepsToReproduce",
      "expectedResult",
      "actualResult",
      "additionalInfo",
      "impact",
    ];

    const labelMap = {
      title: "Title",
      description: "Description",
      environment: "Environment Details",
      priority: "Priority",
      stepsToReproduce: "Steps to Reproduce",
      expectedResult: "Expected Result",
      actualResult: "Actual Result",
      additionalInfo: "Additional Information",
      impact: "Impact",
    };

    const selectedContent = items
      .filter((item) => item !== null)
      .map((item) =>
        orderedKeys
          .map((key) => {
            const label = labelMap[key] || key;
            const value = item[key] ?? "Not Available";
            return `${label}:\n${value}`;
          })
          .join("\n\n")
      )
      .join("\n\n");

    return selectedContent; // ✅ Make sure you return this
  };

  return (
    <AccordionDetails>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            size="small"
            sx={{ textTransform: "none" }}
            onClick={
              allExpanded
                ? handlers.handleCollapseAll
                : handlers.handleExpandAllInput
            }
          >
            {allExpanded ? "Collapse All" : "Expand All"}
          </Button>
        </Box>
      </Box>

      {currentItems.map((item, index) => {
        const globalIndex = (page - 1) * itemsPerPage + index;

        return (
          <Accordion
            key={globalIndex}
            expanded={expandedStates[globalIndex]}
            onChange={handlers.handleChange(globalIndex)}
            sx={{ width: "100%", mb: 0 }}
          >
            <AccordionSummary
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "0 16px",
                backgroundColor: "white",
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {expandedStates[globalIndex] ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
              <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
                <Typography variant="body2">{titles[globalIndex]}</Typography>{" "}
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ paddingLeft: "48px" }}>
              <Grid container spacing={2}>
                {item.title && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Title
                    </Typography>
                    <Typography variant="body2">{item.title}</Typography>
                  </Grid>
                )}

                {item.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Description
                    </Typography>
                    <Typography variant="body2">{item.description}</Typography>
                  </Grid>
                )}

                {item.environment && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Environment Details
                    </Typography>
                    <Typography variant="body2">{item.environment}</Typography>
                  </Grid>
                )}

                {item.priority && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Priority
                    </Typography>
                    <Typography variant="body2">{item.priority}</Typography>
                  </Grid>
                )}
                {item.stepsToReproduce && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Steps to Reproduce
                    </Typography>
                    <Typography variant="body2">
                      {item.stepsToReproduce}
                    </Typography>
                  </Grid>
                )}
                {item.expectedResult && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Expected Result
                    </Typography>
                    <Typography variant="body2">
                      {item.expectedResult}
                    </Typography>
                  </Grid>
                )}

                {item.actualResult && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Actual Result
                    </Typography>
                    <Typography variant="body2">{item.actualResult}</Typography>
                  </Grid>
                )}
                {item.additionalInfo && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Additional Information
                    </Typography>
                    <Typography variant="body2">
                      {item.additionalInfo}
                    </Typography>
                  </Grid>
                )}
                {item.impact && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">
                      Impact
                    </Typography>
                    <Typography variant="body2">{item.impact}</Typography>
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={Math.ceil(items.length / itemsPerPage)}
          page={page}
          onChange={(event, value) => setPage(value)}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginLeft: "20px",
          mb: 1,
          marginTop: "20px",
        }}
      >
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": { color: "#49A3FF" },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": { color: "#FF4842" },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        <CommonCopy contentToCopy={handleCopyClickData()} />
      </Box>
    </AccordionDetails>
  );
};

const EnhancedBugDetailsForm = ({
  editable,
  onSave,
  onCancel,
  index,
  accordianData,
  setAccordianData,
}) => {
  const [bugTitle, setBugTitle] = useState(accordianData[index]["Title"]);
  const [bugDescription, setBugDescription] = useState(
    accordianData[index]["Description"]
  );
  const [severityLevel, setSeverityLevel] = useState(
    accordianData[index]["Severity_Level"]
  );
  const [stepsToReproduce, setStepsToReproduce] = useState(
    accordianData[index]["Steps_To_Reproduce"]
  );
  const [expectedBehaviour, setExpectedBehaviour] = useState(
    accordianData[index]["Expected_Vs_Actual_Behavior"].Expected
  );
  const [actualBehaviour, setActualBehaviour] = useState(
    accordianData[index]["Expected_Vs_Actual_Behavior"].Actual
  );
  const [workaround, setWorkAround] = useState(
    accordianData[index]["Workaround"]
  );
  const [evidenceTBD, setEvidenceTBD] = useState(
    accordianData[index]["Evidences_TBD"]
  );
  const [businessImpact, setBusinessImpact] = useState(
    accordianData[index]["Business_Impact"]
  );
  const [technicalImpact, setTechnicalImpact] = useState(
    accordianData[index]["Technical_Impact"]
  );
  const [possibleCauses, setPossibleCauses] = useState(
    accordianData[index]["Possible_Causes_For_Defect_Leakage"]
  );
  const [additionalInfo, setAdditionalInfo] = useState(
    accordianData[index]["Additional_Information"]
  );
  const [validationAndVerification, setValidationAndVerification] = useState(
    accordianData[index]["Validation_And_Verification_Retesting"]
  );
  const [contextUseCases, setContextUseCases] = useState(
    accordianData[index]["Contextual_Use_Cases_And_Scenarios"]
  );
  const [pcDevTeam, setPCDevTeam] = useState(
    accordianData[index]["Possible_Causes_For_Dev_Team"]
  );

  const [rcFixesDevTeam, setRCFixedDevTeam] = useState(
    accordianData[index]["Recommended_Fixes_For_Dev_Team"]
  );
  const [similarBugs, setSimilarBugs] = useState(
    accordianData[index]["Similar_Bugs"]
  );
  const [idealTestCase, setIdealTestCases] = useState(
    accordianData[index]["Ideal_Test_Case"]
  );

  const renderField = (key, value) => {
    const formatTitle = (title) =>
      title
        .replace(/_/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    const formattedKey = formatTitle(key);

    if (Array.isArray(value)) {
      return (
        <Box key={key} sx={{ mt: 1 }}>
          <Typography variant="body2">{formattedKey}:</Typography>
          {value.map((item, index) => (
            <Typography variant="body2" key={index} sx={{ ml: 0 }}>
              {item}
            </Typography>
          ))}
        </Box>
      );
    } else if (typeof value === "object" && value !== null) {
      return (
        <Box key={key} sx={{ mt: 1 }}>
          <Typography variant="body2">{formattedKey}</Typography>
          {Object.entries(value).map(([nestedKey, nestedValue]) =>
            renderField(nestedKey, nestedValue)
          )}
        </Box>
      );
    } else {
      return (
        <Box key={key} sx={{ mt: 1 }}>
          <Typography variant="body2">
            {formattedKey}: {value}
          </Typography>
        </Box>
      );
    }
  };

  // Function to remove underscores and format titles
  const formatTitle = (title) =>
    title
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  // Function to convert the structured data to a formatted string
  const convertToString = (data) => {
    const result = [];
    for (const [key, value] of Object.entries(data)) {
      const formattedKey = formatTitle(key); // Format the title

      if (Array.isArray(value)) {
        // Handle arrays without adding indices
        const arrayContent = value
          .map(
            (item) =>
              typeof item === "object" && item !== null
                ? convertToString(item) // Recursively process objects in the array
                : item // Handle primitive values directly
          )
          .join("\n");
        result.push(`${formattedKey}:\n${arrayContent}`);
      } else if (typeof value === "object" && value !== null) {
        // Recursively handle nested objects
        result.push(`${formattedKey}: ${convertToString(value)}`);
      } else {
        // Append key-value pairs as a string
        result.push(`${formattedKey}: ${value}`);
      }
    }
    return result.join("\n"); // Join all lines with newlines
  };

  // Generate a single formatted string for the input field
  const formattedText = convertToString(idealTestCase);

  const handleSave = () => {
    const updatedValues = {
      title: bugTitle,
      //   description: bugDescription,
      //   environment: bugEnvironment,
    };
    onSave(updatedValues);
    setAccordianData((prevState) => {
      const savedData = [...prevState];
      const savedBug = savedData[index];
      savedBug["Title"] = bugTitle;
      savedBug["Description"] = bugDescription;
      savedBug["Severity_Level"] = severityLevel;
      savedBug["Steps_To_Reproduce"] = stepsToReproduce;
      savedBug["Expected_Vs_Actual_Behavior"].Expected = expectedBehaviour;
      savedBug["Expected_Vs_Actual_Behavior"].Actual = actualBehaviour;
      savedBug["Workaround"] = workaround;
      savedBug["Evidences_TBD"] = evidenceTBD;
      savedBug["Business_Impact"] = businessImpact;
      savedBug["Technical_Impact"] = technicalImpact;
      savedBug["Possible_Causes_For_Defect_Leakage"] = possibleCauses;
      savedBug["Additional_Information"] = additionalInfo;
      savedBug["Validation_And_Verification_Retesting"] =
        validationAndVerification;
      savedBug["Contextual_Use_Cases_And_Scenarios"] = contextUseCases;
      savedBug["Possible_Causes_For_Dev_Team"] = pcDevTeam;
      savedBug["Recommended_Fixes_For_Dev_Team"] = rcFixesDevTeam;
      savedBug["Similar_Bugs"] = similarBugs;
      savedBug["Ideal_Test_Case"] = idealTestCase;
      savedData[index] = savedBug;
      return savedData;
    });
  };

  // Function to handle Cancel action
  const handleCancel = () => {
    setBugTitle(accordianData[index]["Title"]);
    setBugDescription(accordianData[index]["Description"]);
    setSeverityLevel(accordianData[index]["Severity_Level"]);
    setStepsToReproduce(accordianData[index]["Steps_To_Reproduce"]);
    setExpectedBehaviour(
      accordianData[index]["Expected_Vs_Actual_Behavior"].Expected
    );
    setActualBehaviour(
      accordianData[index]["Expected_Vs_Actual_Behavior"].Actual
    );
    setWorkAround(accordianData[index]["Workaround"]);
    setEvidenceTBD(accordianData[index]["Evidences_TBD"]);
    setBusinessImpact(accordianData[index]["Business_Impact"]);
    setTechnicalImpact(accordianData[index]["Technical_Impact"]);
    setPossibleCauses(
      accordianData[index]["Possible_Causes_For_Defect_Leakage"]
    );
    setAdditionalInfo(accordianData[index]["Additional_Information"]);
    setValidationAndVerification(
      accordianData[index]["Validation_And_Verification_Retesting"]
    );
    setContextUseCases(
      accordianData[index]["Contextual_Use_Cases_And_Scenarios"]
    );
    setPCDevTeam(accordianData[index]["Possible_Causes_For_Dev_Team"]);
    setRCFixedDevTeam(accordianData[index]["Recommended_Fixes_For_Dev_Team"]);
    setSimilarBugs(accordianData[index]["Similar_Bugs"]);
    setIdealTestCases(accordianData[index]["Ideal_Test_Case"]);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Box p={1}>
      {!editable && (
        <Grid container spacing={2}>
          {bugTitle && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Title
              </Typography>
              <Typography variant="body2">{bugTitle}</Typography>
            </Grid>
          )}

          {bugDescription && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Description
              </Typography>
              <Typography variant="body2">{bugDescription}</Typography>
            </Grid>
          )}

          {stepsToReproduce && stepsToReproduce.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Steps to Reproduce
              </Typography>
              <Box sx={{ mt: 1 }}>
                {stepsToReproduce.map((step, index) => (
                  <Typography variant="body2" key={index}>
                    {step}
                  </Typography>
                ))}
              </Box>
            </Grid>
          )}

          {expectedBehaviour && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Expected Result
              </Typography>
              <Typography variant="body2">{expectedBehaviour}</Typography>
            </Grid>
          )}

          {actualBehaviour && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Actual Result
              </Typography>
              <Typography variant="body2">{actualBehaviour}</Typography>
            </Grid>
          )}

          {severityLevel && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Severity Level
              </Typography>
              <Typography variant="body2">{severityLevel}</Typography>
            </Grid>
          )}

          {similarBugs && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Similar Bugs
              </Typography>
              <Typography variant="body2">{similarBugs}</Typography>
            </Grid>
          )}
          {evidenceTBD && evidenceTBD.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Evidence
              </Typography>
              <Box sx={{ mt: 1 }}>
                {evidenceTBD.map((step, index) => (
                  <Typography variant="body2" key={index}>
                    {step}
                  </Typography>
                ))}
              </Box>
            </Grid>
          )}

          {businessImpact && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Business Impact
              </Typography>
              <Typography variant="body2">{businessImpact}</Typography>
            </Grid>
          )}
          {technicalImpact && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Technical Impact
              </Typography>
              <Typography variant="body2">{technicalImpact}</Typography>
            </Grid>
          )}
          {workaround && workaround.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Workaround
              </Typography>
              <Box sx={{ mt: 1 }}>
                {workaround.map((step, index) => (
                  <Typography variant="body2" key={index}>
                    {step}
                  </Typography>
                ))}
              </Box>
            </Grid>
          )}
          {possibleCauses && possibleCauses.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Possible Causes for Defect Leakage
              </Typography>
              <Box sx={{ mt: 1 }}>
                {possibleCauses.map((step, index) => (
                  <Typography variant="body2" key={index}>
                    {step}
                  </Typography>
                ))}
              </Box>
            </Grid>
          )}

          {additionalInfo && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Additional Information
              </Typography>
              <Typography variant="body2">{additionalInfo}</Typography>
            </Grid>
          )}

          {validationAndVerification &&
            validationAndVerification.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Validation and Verification Retesting
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {validationAndVerification.map((step, index) => (
                    <Typography variant="body2" key={index}>
                      {step}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            )}
          {rcFixesDevTeam && rcFixesDevTeam.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Recommended Fixes for Dev Team
              </Typography>
              <Box sx={{ mt: 1 }}>
                {rcFixesDevTeam.map((step, index) => (
                  <Typography variant="body2" key={index}>
                    {step}
                  </Typography>
                ))}
              </Box>
            </Grid>
          )}
          {idealTestCase && (
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                Ideal Test Case
              </Typography>
              {Object.entries(idealTestCase).map(([key, value]) =>
                renderField(key, value)
              )}
            </Grid>
          )}
        </Grid>
      )}
      {editable && (
        <Grid2 container spacing={2}>
          {/* Title */}
          <TextField
            fullWidth
            label="Title"
            value={bugTitle}
            onChange={(e) => setBugTitle(e.target.value)}
            placeholder="Enter the Bug Title"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={bugDescription}
            onChange={(e) => setBugDescription(e.target.value)}
            placeholder="Enter the Bug Description"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Steps to Reproduce */}
          <TextField
            fullWidth
            label="Steps to Reproduce"
            value={stepsToReproduce.join("\n") || ""}
            onChange={(e) => setStepsToReproduce(e.target.value.split("\n"))}
            placeholder="Enter steps to reproduce the bug"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          <TextField
            fullWidth
            label="Expected Behaviour"
            value={expectedBehaviour}
            onChange={(e) => setExpectedBehaviour(e.target.value)}
            placeholder="Describe the expected behaviour"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          <TextField
            fullWidth
            label="Actual Behaviour"
            value={actualBehaviour || ""}
            onChange={(e) => setActualBehaviour(e.target.value)}
            placeholder="Describe the actual behaviour"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          <TextField
            fullWidth
            label="Severity Level"
            value={severityLevel}
            onChange={(e) => setSeverityLevel(e.target.value)}
            placeholder="Severity Level"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          <TextField
            fullWidth
            label="Similar Bugs"
            value={similarBugs}
            onChange={(e) => setSimilarBugs(e.target.value)}
            placeholder="Similar Bugs"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Evidence */}
          <TextField
            fullWidth
            label="Evidence"
            value={evidenceTBD.join("\n") || ""}
            onChange={(e) => setEvidenceTBD(e.target.value.split("\n"))}
            placeholder="Enter any evidence for the bug"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />
          <TextField
            fullWidth
            label="Business Impact"
            value={businessImpact || ""}
            onChange={(e) => setBusinessImpact(e.target.value)}
            placeholder="Describe the business impact"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />
          <TextField
            fullWidth
            label="Technical Impact"
            value={technicalImpact || ""}
            onChange={(e) => setTechnicalImpact(e.target.value)}
            placeholder="Describe the technical impact"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Workaround */}
          <TextField
            fullWidth
            label="Workaround"
            value={workaround.join("\n") || ""}
            onChange={(e) => setWorkAround(e.target.value.split("\n"))}
            placeholder="Enter any workaround for the bug"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Possible Causes for Defect Leakage */}
          <TextField
            fullWidth
            label="Possible Causes for Defect Leakage"
            value={possibleCauses.join("\n") || ""}
            onChange={(e) => setPossibleCauses(e.target.value.split("\n"))}
            placeholder="Describe possible causes for defect leakage"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Additional Information */}
          <TextField
            fullWidth
            label="Additional Information"
            value={additionalInfo || ""}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder="Enter any additional information"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Validation and Verification Retesting */}
          <TextField
            fullWidth
            label="Validation and Verification Retesting"
            value={validationAndVerification.join("\n") || ""}
            onChange={(e) =>
              setValidationAndVerification(e.target.value.split("\n"))
            }
            placeholder="Enter validation and verification retesting steps"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Contextual Use Cases and Scenarios */}
          <TextField
            fullWidth
            label="Contextual Use Cases and Scenarios"
            value={contextUseCases.join("\n") || ""}
            onChange={(e) => setContextUseCases(e.target.value.split("\n"))}
            placeholder="Describe contextual use cases and scenarios"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />

          {/* Possible_Causes_For_Dev_Team */}
          <TextField
            fullWidth
            label="Possible Causes For Dev Team"
            value={pcDevTeam.join("\n") || ""}
            onChange={(e) => setPCDevTeam(e.target.value.split("\n"))}
            placeholder="Describe contextual use cases and scenarios"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />
          <TextField
            fullWidth
            label="Recommended Fixes For Dev Team"
            value={rcFixesDevTeam.join("\n") || ""}
            onChange={(e) => setRCFixedDevTeam(e.target.value.split("\n"))}
            placeholder="Describe contextual use cases and scenarios"
            variant="outlined"
            multiline
            minRows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled={!editable}
          />
          {/* Ideal Test Case Input*/}

          <TextField
            fullWidth
            label="Ideal Test Case"
            value={formattedText}
            onChange={(e) => setIdealTestCases(e.target.value)}
            multiline
            minRows={8}
            placeholder="Enter the Ideal Test Case"
            variant="outlined"
            size="small"
            disabled={!editable}
          />
        </Grid2>
      )}
      {editable && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ mr: 1, textTransform: "none" }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
};

const removeUnderscores = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(removeUnderscores);
  } else if (typeof obj === "object" && obj !== null) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key.replace(/_/g, " "),
        removeUnderscores(value),
      ])
    );
  }
  return obj;
};

const EnhancedMultipleOutputBugs = ({
  items,
  page,
  checkedStates,
  expandedStates,
  handlers,
  setPage,
  showEditIcon = false,
  isEnhanced = false,
  accordianData,
  setAccordianData,
  selectedAccordianData,
  setSelectedAccordianData,
}) => {
  // State to hold updated titles
  const [titles, setTitles] = useState(items.map((item) => item.Title || ""));
  const [editingStates, setEditingStates] = useState(
    Array(items.length).fill(false)
  );
  const currentItems = items.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  const currentPageStartIndex = (page - 1) * itemsPerPage;
  const currentPageEndIndex = currentPageStartIndex + currentItems.length;

  const currentPageCheckedStates = checkedStates.slice(
    currentPageStartIndex,
    currentPageEndIndex
  );
  const currentPageExpandedStates = expandedStates.slice(
    currentPageStartIndex,
    currentPageEndIndex
  );

  const areAllSelected =
    currentPageCheckedStates.every((checked) => checked) &&
    currentPageCheckedStates.length > 0;
  const areAllExpanded =
    currentPageExpandedStates.every((expanded) => expanded) &&
    currentPageExpandedStates.length > 0;
  const handleTitleUpdate = (index, newTitle) => {
    setTitles((prevTitles) => {
      const newTitles = [...prevTitles];
      newTitles[index] = newTitle;
      return newTitles;
    });
  };

  const handleThumbClick = (thumbType) => {
    // Toggle thumbs based on the thumbType (like or dislike)
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleCopyClickData = () => {
    const orderedKeys = [
      "Title",
      "Description",
      "Steps_To_Reproduce",
      "Expected_Result",
      "Actual_Result",
      "Severity_Level",
      "Similar_Bugs",
      "Evidences_TBD",
      "Business_Impact",
      "Technical_Impact",
      "Workaround",
      "Possible_Causes_For_Defect_Leakage",
      "Additional_Information",
      "Validation_And_Verification_Retesting",
      "Recommended_Fixes_For_Dev_Team",
      "Ideal_Test_Case",
    ];

    const labelMap = {
      Title: "Title",
      Description: "Description",
      Steps_To_Reproduce: "Steps to Reproduce",
      Expected_Result: "Expected Result",
      Actual_Result: "Actual Result",
      Severity_Level: "Severity Level",
      Similar_Bugs: "Similar Bugs",
      Evidences_TBD: "Evidence",
      Business_Impact: "Business Impact",
      Technical_Impact: "Technical Impact",
      Workaround: "Workaround",
      Possible_Causes_For_Defect_Leakage: "Possible Causes for Defect Leakage",
      Additional_Information: "Additional Information",
      Validation_And_Verification_Retesting:
        "Validation and Verification Retesting",
      Recommended_Fixes_For_Dev_Team: "Recommended Fixes for Dev Team",
      Ideal_Test_Case: "Ideal Test Case",
    };

    const idealTestCaseLabelMap = {
      Test_Case_ID: "Test Case ID",
      Test_Steps: "Test Steps",
      Expected_Result: "Expected Result",
      "Pass/Fail_criteria": "Pass/Fail Criteria",
      Execution_environment: "Execution Environment",
    };

    const selectedContent = selectedAccordianData
      .filter((item) => item !== null)
      .map((item) => {
        const expected =
          item.Expected_Vs_Actual_Behavior?.Expected || "Not Available";
        const actual =
          item.Expected_Vs_Actual_Behavior?.Actual || "Not Available";

        return orderedKeys
          .map((key) => {
            const label = labelMap[key] || key;
            if (key === "Expected_Result") {
              return `${label}:\n${expected}`;
            }
            if (key === "Actual_Result") {
              return `${label}:\n${actual}`;
            }

            const value = item[key];
            if (!value) return `${label}: Not Available`;

            if (Array.isArray(value)) {
              return `${label}:\n${value.join("\n")}`;
            } else if (typeof value === "object" && value !== null) {
              if (key === "Ideal_Test_Case") {
                return `${label}:\n${Object.entries(value)
                  .map(([k, v]) => {
                    const prettyKey =
                      idealTestCaseLabelMap[k] || k.replace(/_/g, " ");
                    return `${prettyKey}: ${v}`;
                  })
                  .join("\n")}`;
              }

              return `${label}:\n${Object.entries(value)
                .map(
                  ([nestedKey, nestedValue]) => `${nestedKey}: ${nestedValue}`
                )
                .join("\n")}`;
            } else {
              return `${label}:\n${value}`;
            }
          })
          .join("\n\n");
      })
      .join("\n\n");

    return selectedContent; // ✅ CRITICAL LINE
  };

  const [activeThumb, setActiveThumb] = useState(null);
  const [isCopied, setIsCopied] = useState(false);

  return (
    <AccordionDetails>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#49A3FF" }}
            onClick={
              areAllSelected
                ? handlers.handleUnselectAll
                : handlers.handleSelectAll
            }
          >
            {areAllSelected ? "Unselect All" : "Select All"}
          </Button>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#49A3FF" }}
            onClick={
              areAllExpanded
                ? handlers.handleCollapseAll
                : handlers.handleExpandAll
            }
          >
            {areAllExpanded ? "Collapse All" : "Expand All"}
          </Button>
        </Box>
      </Box>

      {currentItems.map((item, index) => {
        const globalIndex = (page - 1) * itemsPerPage + index;
        return (
          <Accordion
            key={globalIndex}
            expanded={expandedStates[globalIndex]}
            onChange={handlers.handleChange(globalIndex)}
            sx={{ width: "100%", mb: 0 }}
          >
            <AccordionSummary
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "0 16px",
                backgroundColor: checkedStates[globalIndex]
                  ? "#d6eaf8"
                  : "white",
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  alignItems: "center",
                  width: "100%", // Ensure full width
                },
              }}
            >
              {/* Left side: Arrow icon, checkbox, and title */}
              <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                {expandedStates[globalIndex] ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowRightIcon />
                )}
                <Checkbox
                  size="small"
                  checked={checkedStates[globalIndex]}
                  onChange={(event) =>
                    handlers.handleCheckboxChange(globalIndex, event)
                  }
                  onClick={(event) => event.stopPropagation()}
                  sx={{ ml: 1 }}
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {titles[globalIndex]}
                </Typography>
              </Box>

              {/* Right side: Edit icon */}
              {showEditIcon && (
                <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
                  <Tooltip title="Click to edit" placement="right" arrow>
                    <Button
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation();

                        const isCurrentlyEditing = editingStates[globalIndex];

                        // Toggle editing state
                        setEditingStates((prev) => {
                          const newEditingStates = [...prev];
                          newEditingStates[globalIndex] =
                            !newEditingStates[globalIndex];
                          return newEditingStates;
                        });

                        // Toggle accordion expansion based on editing state
                        if (!isCurrentlyEditing) {
                          // Entering edit mode - expand accordion
                          handlers.handleChange(globalIndex)(null, true);
                        } else {
                          // Exiting edit mode - collapse accordion
                          handlers.handleChange(globalIndex)(null, false);
                        }
                      }}
                      sx={{
                        minWidth: "auto",
                        padding: "4px",
                      }}
                    >
                      <PenIcon style={{ width: 18, height: 18 }} />
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ paddingLeft: "48px" }}>
              <EnhancedBugDetailsForm
                bugDetails={item}
                editable={editingStates[globalIndex]}
                onSave={(updatedValues) => {
                  handleTitleUpdate(globalIndex, updatedValues.title); // Update title in state
                  setEditingStates((prev) => {
                    const newEditingStates = [...prev];
                    newEditingStates[globalIndex] = false;
                    return newEditingStates;
                  });
                }}
                onCancel={() => {
                  setEditingStates((prev) => {
                    const newEditingStates = [...prev];
                    newEditingStates[globalIndex] = false;
                    return newEditingStates;
                  });
                }}
                index={index}
                accordianData={accordianData}
                setAccordianData={setAccordianData}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={Math.ceil(items.length / itemsPerPage)}
          page={page}
          onChange={(event, value) => setPage(value)}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", marginLeft: "20px" }}>
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": { color: "#49A3FF" },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2,
              "&:hover": { color: "#FF4842" },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} />
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} />
            )}
          </Box>
        </Tooltip>

        <CommonCopy contentToCopy={handleCopyClickData()} />
      </Box>
    </AccordionDetails>
  );
};

const MultipleOutputBugReport = () => {
  const location = useLocation();
  const [bugDetails, setBugDetails] = useState(location.state); // This will contain the data passed via navigate from BugReport Input Page

  const [selectedBugDetails, setSelectedBugDetails] = useState([
    ...bugDetails.multipleOutputs,
  ]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const [bugDetailsExpanded, setBugDetailsExpanded] = useState(false);
  const [enhancedDescriptionExpanded, setEnhancedDescriptionExpanded] =
    useState(true);

  const [noneSelectionPopOpen, setNoneSelectionPopOpen] = useState(false);
  const [message, setMessage] = useState(null);
  const [openExportPopup, setOpenExportPopUp] = useState(false);
  const [openProjectPage, setOpenProjectPage] = useState(false);

  const [bugDetailsPage, setBugDetailsPage] = useState(1);
  const [enhancedPage, setEnhancedPage] = useState(1);
  const [bugDetailsCheckedStates, setBugDetailsCheckedStates] = useState(
    Array(bugDetails.multipleInputs.length).fill(false)
  );
  selectedBugDetails.map(
    (eachSelectedDetail, bugIndex) =>
      JSON.stringify(eachSelectedDetail) ===
      JSON.stringify(bugDetails[bugIndex])
  );
  const [enhancedCheckedStates, setEnhancedCheckedStates] = useState(
    Array(bugDetails.multipleOutputs.length).fill(false)
  );
  const [bugDetailsExpandedStates, setBugDetailsExpandedStates] = useState(
    Array(100).fill(false)
  );
  const [enhancedExpandedStates, setEnhancedExpandedStates] = useState(
    Array(100).fill(false)
  );
  const [openExportPage, setOpenExportPage] = useState(false);

  // Map the entire object instead of just the title
  const bugDetailsItems = Array.from(
    { length: bugDetails.multipleInputs.length },
    (_, index) => bugDetails.multipleInputs[index] // This will include both title and description
  );

  const enhancedDescriptionItems = Array.from(
    { length: bugDetails.multipleOutputs.length },
    (_, index) => bugDetails.multipleOutputs[index] // This will include both title and description
  );

  const formattedBugDetails = useMemo(() => {
    const formattingBugDetails = [];

    selectedBugDetails.forEach((eachBug, index) => {
      if (!eachBug || !enhancedCheckedStates[index]) {
        return;
      }

      const flattenBug = {};
      Object.entries(eachBug).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          flattenBug[key] = flattenArray(value);
        } else if (typeof value === "object" && !!value) {
          flattenBug[key] = flattenObject(value);
        } else {
          flattenBug[key] = value;
        }
      });
      formattingBugDetails.push(flattenBug);
    });

    return formattingBugDetails;
  }, [selectedBugDetails, enhancedCheckedStates]);

  const selectedItemsForDownload = useMemo(() => {
    return selectedBugDetails.filter(
      (bug, index) =>
        bug !== null && bug !== undefined && enhancedCheckedStates[index]
    );
  }, [selectedBugDetails, enhancedCheckedStates]);

  const createAccordionHandlers = (
    setCheckedStates,
    setExpandedStates,
    checkedStates,
    expandedStates,
    accordianData,
    setAccordianData,
    selectedAccordianData,
    setSelectedAccordianData
  ) => ({
    handleChange: (index) => (event, isExpanded) => {
      setExpandedStates((prev) => {
        const newStates = [...prev];
        newStates[index] = isExpanded;
        return newStates;
      });
    },

    handleCheckboxChange: (index, event) => {
      event.stopPropagation();
      setCheckedStates((prevStates) => {
        const newStates = [...prevStates];
        newStates[index] = !newStates[index];
        return newStates;
      });
      const updatedSelection = [...selectedAccordianData];
      if (event.target.checked) {
        updatedSelection[index] = accordianData[index];
      } else {
        updatedSelection[index] = null;
      }
      setSelectedAccordianData(updatedSelection);
    },

    handleSelectAll: () => {
      const newCheckedStates = Array(accordianData.length).fill(true);
      setCheckedStates(newCheckedStates);
      setSelectedAccordianData([...accordianData]);
    },

    handleUnselectAll: () => {
      const newCheckedStates = Array(accordianData.length).fill(false);
      setCheckedStates(newCheckedStates);
      setSelectedAccordianData(Array(accordianData.length).fill(null));
    },

    handleExpandAll: () => {
      setExpandedStates(Array(accordianData.length).fill(true));
    },

    handleExpandAllInput: () => {
      setExpandedStates(Array(accordianData.multipleOutputs.length).fill(true));
    },

    handleCollapseAll: () => {
      setExpandedStates(Array(accordianData.length).fill(false));
    },
  });
  const bugDetailsHandlers = createAccordionHandlers(
    setBugDetailsCheckedStates,
    setBugDetailsExpandedStates,
    bugDetailsCheckedStates,
    bugDetailsExpandedStates,
    bugDetails,
    setBugDetails
  );

  const enhancedHandlers = createAccordionHandlers(
    setEnhancedCheckedStates,
    setEnhancedExpandedStates,
    enhancedCheckedStates,
    enhancedExpandedStates,
    enhancedDescriptionItems,
    setBugDetails,
    selectedBugDetails,
    setSelectedBugDetails
  );

  const handleExportClick = () => {
    setOpenProjectPage({
      open: true,
    });
  };

  const handleExportBugs = async () => {
    if (selectedBugDetails.every((eachUserStory) => !eachUserStory)) {
      setNoneSelectionPopOpen(true);
      return;
    }

    try {
      const notExported = [];

      for (const [index, eachBugReport] of selectedBugDetails.entries()) {
        if (eachBugReport === null || !eachBugReport) {
          continue;
        }

        const requestData = {
          ...eachBugReport,
          key: bugDetails.multipleInputs[index]["key"],
        };

        try {
          const exported = await exportBugReportToJira(requestData);
        } catch (error) {
          notExported.push(bugDetails[index]["key"]);
        }
      }

      if (notExported.length > 0) {
        setMessage(
          `Could not export the following bugs to JIRA: \n ${notExported.join(
            ", "
          )}`
        );
      } else {
        setMessage("Exported selected user stories to JIRA");
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        bgcolor: "#f6f3f2",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        overflowY: "auto",
        width: "100%",
        minHeight: "80vh",
        height: "80vh",
        position: "relative",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",

        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "4px",
          minHeight: "20px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
      }}
    >
      <Box
        className="bug-report-container"
        sx={{ bgcolor: "#F6F3F2", minHeight: "100vh", p: 2 }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              // maxWidth: 600,
              margin: "0 auto",
            }}
          >
            <Box sx={{ flexGrow: 1, width: "100%" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                    color: "var(--Schemes-Secondary, #000)",
                    fontFamily: "Manrope, sans-serif",
                  }}
                >
                  <Box sx={{ display: "flex" }}>
                    <AutomateIcon sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Generate detailed and insightful bug reports with ease
                    </Typography>
                  </Box>
                  {/* <FullscreenToggle /> */}
                </Box>
              </Box>

              {/* //Multiple Input Bug Details Accordion */}
              <CommonAccordion
                summary={<>Bug Details ({bugDetailsItems.length})</>}
                details={
                  <Box
                    sx={{
                      height: "40vh",
                      overflowY: "auto",
                      scrollbarWidth: "thin", // For Firefox
                      "&::-webkit-scrollbar": {
                        width: "4px", // Thin scrollbar width
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#ccc", // Scroll thumb color
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <RenderMultipleInputBugDetils
                      items={bugDetailsItems}
                      page={bugDetailsPage}
                      expandedStates={bugDetailsExpandedStates}
                      handlers={bugDetailsHandlers}
                      setPage={setBugDetailsPage}
                      isEnhanced={false}
                    />
                  </Box>
                }
                type="input"
                defaultExpanded={bugDetailsExpanded}
                fullScreenMode={true}
                buttons={[]} // If no buttons are needed, keep it empty
              />

              {/* {showEnhancedDescription && ( */}
              <CommonAccordion
                summary={`Enhanced Bug Descriptions (${enhancedDescriptionItems.length})`}
                details={
                  <Box
                    sx={{
                      height: "40vh",
                      overflowY: "auto",
                      scrollbarWidth: "thin", // For Firefox
                      "&::-webkit-scrollbar": {
                        width: "4px", // Thin scrollbar width
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#ccc", // Scroll thumb color
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <EnhancedMultipleOutputBugs
                      items={enhancedDescriptionItems}
                      page={enhancedPage}
                      checkedStates={enhancedCheckedStates}
                      expandedStates={enhancedExpandedStates}
                      handlers={enhancedHandlers}
                      setPage={setEnhancedPage}
                      showEditIcon={true}
                      isEnhanced={true}
                      accordianData={enhancedDescriptionItems}
                      setAccordianData={setSelectedBugDetails}
                      selectedAccordianData={selectedBugDetails}
                      setSelectedAccordianData={setSelectedBugDetails}
                    />
                  </Box>
                }
                buttons={[
                  {
                    label: "Export",
                    onClick: () => setOpenExportPage(true),
                    variant: "text",
                    startIcon: enhancedCheckedStates.some(
                      (checked) => checked
                    ) ? (
                      <ExportButton />
                    ) : (
                      <ExportButton1 />
                    ),
                    disabled: !enhancedCheckedStates.some((checked) => checked),
                    sx: {
                      color: enhancedCheckedStates.some((checked) => checked)
                        ? "#49A3FF"
                        : "#ccc",
                      textTransform: "none",
                      float: "left",
                      "&:disabled": {
                        color: "#ccc",
                        cursor: "not-allowed",
                      },
                      mb: 3,
                    },
                  },
                  {
                    label: "Download",
                    onClick: () => setDownloadDialogOpen(true),
                    variant: "contained",
                    startIcon: <FileDownloadOutlinedIcon />,
                    disabled: !enhancedCheckedStates.some((checked) => checked),
                    sx: {
                      backgroundColor: enhancedCheckedStates.some(
                        (checked) => checked
                      )
                        ? "#49A3FF"
                        : "#ccc",
                      textTransform: "none",
                      float: "right",
                      "&:disabled": {
                        color: "#666",
                        cursor: "not-allowed",
                      },
                      mb: 3,
                    },
                  },
                ]}
                type="output"
                defaultExpanded={enhancedDescriptionExpanded}
                fullScreenMode={true}
              />

              <CommonDownload
                open={downloadDialogOpen}
                onClose={() => setDownloadDialogOpen(false)}
                serviceName="Bug Report"
                wordData={createWordData(
                  removeUnderscores(selectedItemsForDownload), // Changed from selectedBugDetails.filter(...)
                  "Bug Report"
                )}
                serviceResponse={[
                  {
                    heading: "Enhanced Bug Report",
                    type: "subParagraphs",
                    data: removeUnderscores(
                      selectedItemsForDownload.map((bug, index) => ({
                        // Changed from selectedBugDetails.filter(...)
                        ...formatDataForPDF(bug),
                      }))
                    ),
                  },
                ]}
                documents={["docx", "pdf"]}
              />

              {/* )} */}
            </Box>
          </Box>
        </Container>
        {/* Centered Disclaimer */}
        <Box
          component="footer"
          sx={{
            width: "100%",
            // maxWidth: "601px",
            backgroundColor: "#f6f3f2",
            textAlign: "right",
            paddingX: "5px",
            position: "fixed",
            bottom: "0",
            zIndex: "99",
            left: "46%", // Shift the Box a bit more to the right
            transform: "translateX(-50%)", // Adjust for centering
          }}
        >
          <Typography variant="caption" color="textSecondary">
            Use QMentisAI™ with human supervision, as with all generative AI
            tools.
          </Typography>
        </Box>

        <SelectionErrorPopUp
          open={noneSelectionPopOpen}
          onClose={() => setNoneSelectionPopOpen(false)}
        />

        {/* {openExportPopup && (
          <ConfirmationDialog
            onClick={handleExportBugs}
            setOpenExportPopUp={setOpenExportPopUp}
            message={message}
            setMessage={setMessage}
            text={
              "Exported bug description will replace and update the bug details"
            }
            successMessage={
              "Enhanced Bug descriptions sucessfully exported to jira"
            }
          />
        )} */}
        {openExportPage && (
          <ALMConnectionPage
            issueType={"Bugs"}
            setPageCloser={setOpenExportPage}
            taskType="Export"
            exportData={formattedBugDetails?.filter((item) => item)}
            inputData={bugDetails.multipleInputs}
            almConfig={location.state?.almConfig}
            confluenceConfig={location.state?.confluenceConfig}
          />
        )}
      </Box>
    </Box>
  );
};

export default MultipleOutputBugReport;
